import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { PackagingHeavyMetalsAuditRunResults } from './PackagingHeavyMetalsAuditRunResults'
import {
  type PackagingHeavyMetalsAuditResult,
  useRunPackagingItemHeavyMetalsAudit,
} from '@/shared/hooks/api/packagingSuppliers'

type PackagingItemHeavyMetalsAuditRunSheetProps = {
  supplierId: string
  itemId: string
  open?: boolean
  onOpenChange?: (open: boolean) => void
  latestEntryIsManualOverride?: boolean
}

export function PackagingItemHeavyMetalsAuditRunSheet({
  supplierId,
  itemId,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  latestEntryIsManualOverride = false,
}: PackagingItemHeavyMetalsAuditRunSheetProps) {
  const { t } = useTranslation(['packagingSuppliers', 'common'])
  const [internalOpen, setInternalOpen] = useState(false)
  const [replaceOverrideConfirmOpen, setReplaceOverrideConfirmOpen] =
    useState(false)
  const [latestResult, setLatestResult] =
    useState<PackagingHeavyMetalsAuditResult | null>(null)
  const heavyMetalsAuditMutation = useRunPackagingItemHeavyMetalsAudit(
    supplierId,
    itemId
  )

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const setOpen = (nextOpen: boolean) => {
    if (isControlled) {
      controlledOnOpenChange?.(nextOpen)
    } else {
      setInternalOpen(nextOpen)
    }
  }

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setLatestResult(null)
      heavyMetalsAuditMutation.reset()
    }
  }

  const handleRun = async () => {
    setLatestResult(null)
    try {
      const nextResult = await heavyMetalsAuditMutation.mutateAsync()
      setLatestResult(nextResult)
    } catch {
      // isError state drives the sheet error message.
    }
  }

  const handleFooterClick = () => {
    if (latestResult) {
      handleOpenChange(false)
      return
    }
    if (latestEntryIsManualOverride) {
      setReplaceOverrideConfirmOpen(true)
      return
    }
    void handleRun()
  }

  let footerButtonLabel = t('itemDetail.heavyMetalsAudit.run')
  if (heavyMetalsAuditMutation.isPending) {
    footerButtonLabel = t('itemDetail.heavyMetalsAudit.running')
  } else if (latestResult) {
    footerButtonLabel = t('common:actions.close')
  }

  return (
    <>
      <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className='flex flex-col sm:max-w-lg'>
        <SheetHeader>
          <SheetTitle>{t('itemDetail.heavyMetalsAudit.runSheetTitle')}</SheetTitle>
          <SheetDescription>
            {t('itemDetail.heavyMetalsAudit.runSheetDescription')}
          </SheetDescription>
        </SheetHeader>
        <div className='min-h-0 flex-1 space-y-4 overflow-y-auto py-4'>
          {heavyMetalsAuditMutation.isError ? (
            <p className='text-sm text-destructive'>
              {t('itemDetail.heavyMetalsAudit.runFailed')}
            </p>
          ) : null}
          {latestResult ? (
            <PackagingHeavyMetalsAuditRunResults result={latestResult} />
          ) : null}
        </div>
        <SheetFooter>
          <Button
            type='button'
            onClick={handleFooterClick}
            disabled={heavyMetalsAuditMutation.isPending}
          >
            {footerButtonLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>

      <AlertDialog
        open={replaceOverrideConfirmOpen}
        onOpenChange={setReplaceOverrideConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('itemDetail.manualOverride.replaceOverrideTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('itemDetail.manualOverride.replaceOverrideBody')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('itemDetail.manualOverride.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault()
                setReplaceOverrideConfirmOpen(false)
                void handleRun()
              }}
            >
              {t('itemDetail.heavyMetalsAudit.run')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
